import type { ReactNode } from 'react';
import { Banknote, CircleDollarSign, Landmark, TrendingUp, TrendingDown, type LucideIcon } from 'lucide-react';

export type MoneyChipVariant = 'ask' | 'pay' | 'gap' | 'fee' | 'ours' | 'ins' | 'gain' | 'loss';

/**
 * "P" DE PROACTIVE E "I" DE INSURANCE, EN UN CÍRCULO.
 *
 * Pedido del dueño (2026-08-20): en el board los dos montos se distinguían solo por el color del
 * fondo —ámbar contra verde— y nadie sabía cuál era cuál ("lo digo así porque no se entiende").
 * Un billete y un banco tampoco lo decían: los dos son íconos de plata. La letra sí, y no hay que
 * aprenderla.
 *
 * Va dibujada a mano y no como ícono de la librería porque lucide no trae letras en círculo.
 * `currentColor` en todo, así que hereda el color del chip y funciona igual en dark.
 */
function CircledLetter({ letter }: { letter: string }) {
  return (
    <svg viewBox="0 0 16 16" width={12} height={12} aria-hidden="true" style={{ flex: '0 0 auto' }}>
      <circle cx="8" cy="8" r="6.7" fill="none" stroke="currentColor" strokeWidth="1.5" />
      <text x="8" y="8" textAnchor="middle" dominantBaseline="central"
        fontSize="8.5" fontWeight="800" fill="currentColor" fontFamily="inherit">{letter}</text>
    </svg>
  );
}

/** Marca por defecto de cada variante (mockup de Supplements · Review Estimate). */
const DEFAULT_MARK: Record<MoneyChipVariant, () => ReactNode> = {
  ask: () => <Banknote size={11} strokeWidth={2} />, // lo que pedimos
  pay: () => <Landmark size={11} strokeWidth={2} />, // lo que el seguro paga
  gap: () => <TrendingUp size={11} strokeWidth={2} />, // la diferencia
  fee: () => <CircleDollarSign size={11} strokeWidth={2} />, // nuestro fee
  ours: () => <CircledLetter letter="P" />, // NUESTRO estimado (Proactive)
  ins: () => <CircledLetter letter="I" />, // el del seguro (Insurance)
  gain: () => <TrendingUp size={11} strokeWidth={2} />, // vamos ganando
  loss: () => <TrendingDown size={11} strokeWidth={2} />, // vamos perdiendo
};

/**
 * Chip monoespaciado para montos (Geist Mono, principio 4 del rediseño).
 * Variantes ask/pay/gap/fee como el mockup de Supplements; `ours`/`ins`/`gain`/`loss` son las de
 * la fila de Supplements (morado Proactive, azul del seguro, verde/rojo según se gane o se
 * pierda). Colores en styles.css (.mchip.*).
 */
export function MoneyChip({
  variant,
  icon,
  title,
  children,
}: {
  variant: MoneyChipVariant;
  /** Sobrescribe la marca por defecto con un ícono de lucide. */
  icon?: LucideIcon;
  title?: string;
  children: ReactNode;
}) {
  const Icon = icon;
  return (
    <span className={`mchip ${variant}`} title={title}>
      {Icon ? <Icon size={11} strokeWidth={2} /> : DEFAULT_MARK[variant]()}
      {children}
    </span>
  );
}
